/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Payment plan editor — /real-estate/payment-plans/:id and /real-estate/payment-plans/new
 *
 * GET/POST/PUT/DELETE /re/payment-plans[/{id}] (milestones replaced wholesale on PUT),
 * POST /re/payment-plans/preview for a sample price.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, FormControlLabel, Grid, Icon, IconButton, InputAdornment, MenuItem, Skeleton, Switch, TextField, Tooltip,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import InfoCard from './components/ReInfoCard';
import { usePermission } from 'auth/usePermission';
import RePage, { LoadError, useSnack } from './components/RePage';
import { RePicker } from './components/Pickers';
import { DateField } from './components/Widgets';
import SchedulePreview, { usePlanPreview } from './components/SchedulePreview';
import {
  MILESTONE_TRIGGERS, TRIGGER_LABELS, errorMessage, formatPct, round2, todayIso, toNumber, useReApi, useReGet,
} from './api';
import { defaultCurrency } from '../variant';
import { Pill, useIsReV2 } from '../v2/parts';

let keySeq = 0;
const row = (label, percentage, trigger_type, trigger_value = '', trigger_date = '') => {
  keySeq += 1;
  return { key: `m${keySeq}`, label, percentage: String(percentage), trigger_type, trigger_value: trigger_value === '' ? '' : String(trigger_value), trigger_date };
};

const TEMPLATES = [
  {
    name: '60/40',
    post: false,
    build: () => [
      row('Down payment', 20, 'ON_BOOKING'),
      row('20% construction', 10, 'CONSTRUCTION_PCT', 20),
      row('40% construction', 10, 'CONSTRUCTION_PCT', 40),
      row('60% construction', 10, 'CONSTRUCTION_PCT', 60),
      row('80% construction', 10, 'CONSTRUCTION_PCT', 80),
      row('On handover', 40, 'ON_HANDOVER'),
    ],
  },
  {
    name: '50/50 on handover',
    post: false,
    build: () => [
      row('Down payment', 10, 'ON_BOOKING'),
      row('Within 90 days', 10, 'DAYS_AFTER_BOOKING', 90),
      row('30% construction', 10, 'CONSTRUCTION_PCT', 30),
      row('50% construction', 10, 'CONSTRUCTION_PCT', 50),
      row('70% construction', 10, 'CONSTRUCTION_PCT', 70),
      row('On handover', 50, 'ON_HANDOVER'),
    ],
  },
  {
    name: '1% monthly ×60 + 40% handover',
    post: false,
    build: () => [
      row('Month 1 (on booking)', 1, 'ON_BOOKING'),
      // No "months after booking" trigger exists, so months are 30-day steps.
      ...Array.from({ length: 59 }, (_, i) => row(`Month ${i + 2}`, 1, 'DAYS_AFTER_BOOKING', 30 * (i + 1))),
      row('On handover', 40, 'ON_HANDOVER'),
    ],
  },
  {
    name: 'Post-handover 3 years',
    post: true,
    build: () => {
      const q = round2(40 / 12);
      return [
        row('Down payment', 10, 'ON_BOOKING'),
        row('25% construction', 10, 'CONSTRUCTION_PCT', 25),
        row('50% construction', 10, 'CONSTRUCTION_PCT', 50),
        row('75% construction', 10, 'CONSTRUCTION_PCT', 75),
        row('On handover', 20, 'ON_HANDOVER'),
        ...Array.from({ length: 12 }, (_, i) => row(
          `Post-handover Q${i + 1}`,
          i === 11 ? round2(40 - q * 11) : q,
          'MONTHS_AFTER_HANDOVER',
          3 * (i + 1),
        )),
      ];
    },
  },
];

const needsValue = (t) => ['DAYS_AFTER_BOOKING', 'CONSTRUCTION_PCT', 'MONTHS_AFTER_HANDOVER'].includes(t);
const valueLabel = (t) => ({ DAYS_AFTER_BOOKING: 'Days', CONSTRUCTION_PCT: 'Constr. %', MONTHS_AFTER_HANDOVER: 'Months' }[t] || '');

const toPayloadMilestones = (rows) => rows.map((m, i) => ({
  sequence: i + 1,
  label: m.label.trim(),
  percentage: toNumber(m.percentage),
  trigger_type: m.trigger_type,
  trigger_value: needsValue(m.trigger_type) ? toNumber(m.trigger_value) : null,
  trigger_date: m.trigger_type === 'FIXED_DATE' ? (m.trigger_date || null) : null,
}));

function MilestoneRow({ m, idx, count, update, remove, move, disabled }) {
  return (
    <Grid container spacing={1} alignItems="center" sx={{ py: 0.5, borderBottom: '1px solid #EEF1F6' }}>
      <Grid item xs={1} md={0.5}>
        <MDTypography variant="caption" color="text">{idx + 1}</MDTypography>
      </Grid>
      <Grid item xs={11} md={3.5}>
        <TextField fullWidth size="small" placeholder="Label" value={m.label} disabled={disabled} onChange={(e) => update(m.key, { label: e.target.value })} />
      </Grid>
      <Grid item xs={4} md={1.5}>
        <TextField
          fullWidth size="small" type="number" value={m.percentage} disabled={disabled}
          onChange={(e) => update(m.key, { percentage: e.target.value })}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        />
      </Grid>
      <Grid item xs={8} md={3}>
        <TextField
          select fullWidth size="small" value={m.trigger_type} disabled={disabled}
          onChange={(e) => update(m.key, { trigger_type: e.target.value })}
        >
          {MILESTONE_TRIGGERS.map((t) => <MenuItem key={t} value={t}>{TRIGGER_LABELS[t]}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={8} md={2}>
        {needsValue(m.trigger_type) && (
          <TextField fullWidth size="small" type="number" label={valueLabel(m.trigger_type)} value={m.trigger_value} disabled={disabled} onChange={(e) => update(m.key, { trigger_value: e.target.value })} />
        )}
        {m.trigger_type === 'FIXED_DATE' && (
          <DateField label="Date" value={m.trigger_date} disabled={disabled} onChange={(v) => update(m.key, { trigger_date: v || '' })} sx={{ width: '100%' }} />
        )}
      </Grid>
      <Grid item xs={4} md={1.5} sx={{ textAlign: 'right' }}>
        {!disabled && (
          <>
            <IconButton size="small" disabled={idx === 0} onClick={() => move(idx, -1)}><Icon fontSize="small">arrow_upward</Icon></IconButton>
            <IconButton size="small" disabled={idx === count - 1} onClick={() => move(idx, 1)}><Icon fontSize="small">arrow_downward</Icon></IconButton>
            <Tooltip title="Remove"><IconButton size="small" onClick={() => remove(m.key)}><Icon fontSize="small">delete_outline</Icon></IconButton></Tooltip>
          </>
        )}
      </Grid>
    </Grid>
  );
}

export default function PaymentPlanDetail() {
  const isV2 = useIsReV2();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { api } = useReApi();
  const [snack, notify] = useSnack();
  const { canCreate, canEdit, canDelete } = usePermission('re_payment_plans');
  const writable = isNew ? canCreate : canEdit;
  const plan = useReGet(isNew ? null : `/re/payment-plans/${id}`);

  const [head, setHead] = useState({ name: '', project_id: null, description: '', is_post_handover: false, is_active: true });
  const [rows, setRows] = useState(() => [row('Down payment', 100, 'ON_BOOKING')]);
  const [samplePrice, setSamplePrice] = useState('1000000');
  const [sampleDate, setSampleDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const p = plan.data;
    if (!p) return;
    setHead({
      name: p.name || '', project_id: p.project_id || null, description: p.description || '',
      is_post_handover: Boolean(p.is_post_handover), is_active: p.is_active !== false,
    });
    const ms = [...(p.milestones || [])].sort((a, b) => a.sequence - b.sequence);
    setRows(ms.map((m) => row(m.label, m.percentage, m.trigger_type, m.trigger_value ?? '', m.trigger_date || '')));
  }, [plan.data]);

  const total = useMemo(() => round2(rows.reduce((a, m) => a + (toNumber(m.percentage) || 0), 0)), [rows]);
  const totalOk = Math.abs(total - 100) <= 0.01;

  const rowProblems = useMemo(() => rows.map((m) => {
    if (!m.label.trim()) return 'label';
    const pct = toNumber(m.percentage);
    if (pct === null || pct <= 0) return 'percentage';
    if (needsValue(m.trigger_type) && toNumber(m.trigger_value) === null) return 'trigger value';
    if (m.trigger_type === 'CONSTRUCTION_PCT' && (toNumber(m.trigger_value) < 0 || toNumber(m.trigger_value) > 100)) return 'construction %';
    if (m.trigger_type === 'FIXED_DATE' && !m.trigger_date) return 'date';
    return null;
  }), [rows]);
  const firstProblem = rowProblems.findIndex(Boolean);

  const previewBody = totalOk && firstProblem === -1 && toNumber(samplePrice)
    ? { milestones: toPayloadMilestones(rows), net_price: toNumber(samplePrice), booking_date: sampleDate, project_id: head.project_id || undefined }
    : null;
  const preview = usePlanPreview(previewBody, 600);

  const update = (key, patch) => setRows((rs) => rs.map((m) => (m.key === key ? { ...m, ...patch } : m)));
  const remove = (key) => setRows((rs) => rs.filter((m) => m.key !== key));
  const move = (idx, dir) => setRows((rs) => {
    const copy = [...rs];
    const [x] = copy.splice(idx, 1);
    copy.splice(idx + dir, 0, x);
    return copy;
  });

  const applyTemplate = (t) => {
    // eslint-disable-next-line no-alert
    if (rows.length > 1 && !window.confirm(`Replace the current milestones with "${t.name}"?`)) return;
    setRows(t.build());
    setHead((h) => ({ ...h, is_post_handover: t.post, name: h.name || t.name }));
  };

  const save = async () => {
    setErr(null);
    if (!head.name.trim()) {
      setErr('The plan needs a name.');
      return;
    }
    if (!rows.length) {
      setErr('Add at least one milestone.');
      return;
    }
    if (firstProblem !== -1) {
      setErr(`Milestone ${firstProblem + 1} is missing its ${rowProblems[firstProblem]}.`);
      return;
    }
    if (!totalOk) {
      setErr(`Milestones total ${formatPct(total, 2)}; they must total exactly 100%.`);
      return;
    }
    setSaving(true);
    const body = {
      name: head.name.trim(),
      project_id: head.project_id || null,
      description: head.description || null,
      is_post_handover: head.is_post_handover,
      is_active: head.is_active,
      milestones: toPayloadMilestones(rows),
    };
    try {
      if (isNew) {
        const res = await api.post('/re/payment-plans', body);
        navigate(`/real-estate/payment-plans/${res.data.id}`, { replace: true });
      } else {
        await api.put(`/re/payment-plans/${id}`, body);
        notify('Payment plan saved');
        plan.reload();
      }
    } catch (e) {
      setErr(errorMessage(e, 'Could not save the plan'));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this payment plan?')) return;
    try {
      await api.delete(`/re/payment-plans/${id}`);
      navigate('/real-estate/payment-plans');
    } catch (e) {
      notify.error(e, 'Could not delete the plan');
    }
  };

  if (!isNew && plan.loading && !plan.data) {
    return <RePage title="Payment plan" onBack={() => navigate('/real-estate/payment-plans')}><Skeleton variant="rectangular" height={300} /></RePage>;
  }
  if (!isNew && !plan.data) {
    return <RePage title="Payment plan" onBack={() => navigate('/real-estate/payment-plans')}><LoadError error={plan.error} onRetry={plan.reload} /></RePage>;
  }

  return (
    <RePage
      title={isNew ? 'New payment plan' : head.name || 'Payment plan'}
      subtitle={head.project_id ? 'Project-specific plan' : 'Global plan (available to every project)'}
      onBack={() => navigate('/real-estate/payment-plans')}
      actions={(
        <>
          {!isNew && canDelete && <MDButton variant="outlined" color="error" size="small" onClick={del}>Delete</MDButton>}
          {writable && (
            <MDButton variant="gradient" color="info" size="small" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create plan' : 'Save'}
            </MDButton>
          )}
        </>
      )}
    >
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <InfoCard icon="tune" title="Plan">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Name" value={head.name} disabled={!writable} onChange={(e) => setHead({ ...head, name: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <RePicker path="/re/projects" label="Project (empty = global)" value={head.project_id} disabled={!writable} onChange={(v) => setHead({ ...head, project_id: v })} sx={{ width: '100%' }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Description" value={head.description} disabled={!writable} onChange={(e) => setHead({ ...head, description: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={head.is_post_handover} disabled={!writable} onChange={(e) => setHead({ ...head, is_post_handover: e.target.checked })} />} label="Post-handover plan" />
                <FormControlLabel control={<Switch checked={head.is_active} disabled={!writable} onChange={(e) => setHead({ ...head, is_active: e.target.checked })} />} label="Active" />
              </Grid>
            </Grid>
          </InfoCard>

          <MDBox mt={2}>
            <InfoCard
              icon="format_list_numbered"
              title="Milestones"
              headerAction={isV2 ? (
                <Pill tone={totalOk ? 'success' : 'danger'}>
                  {`Total ${formatPct(total, 2)}${totalOk ? '' : ` · ${total < 100 ? `${formatPct(round2(100 - total), 2)} to go` : `${formatPct(round2(total - 100), 2)} over`}`}`}
                </Pill>
              ) : (
                <MDBox
                  component="span"
                  sx={{
                    px: 1.25, py: 0.25, borderRadius: 999, fontWeight: 700, fontSize: '0.8rem',
                    bgcolor: totalOk ? '#D7F0E0' : '#FFDAD6', color: totalOk ? '#1F8A56' : '#BA1A1A',
                  }}
                >
                  {`Total ${formatPct(total, 2)}${totalOk ? '' : ` · ${total < 100 ? `${formatPct(round2(100 - total), 2)} to go` : `${formatPct(round2(total - 100), 2)} over`}`}`}
                </MDBox>
              )}
            >
              {writable && (
                <MDBox display="flex" flexWrap="wrap" gap={1} mb={2} alignItems="center">
                  <MDTypography variant="caption" color="text" mr={0.5}>Templates:</MDTypography>
                  {TEMPLATES.map((t) => (
                    <MDButton key={t.name} size="small" variant="outlined" color="info" onClick={() => applyTemplate(t)}>{t.name}</MDButton>
                  ))}
                </MDBox>
              )}
              <MDBox sx={{ maxHeight: 520, overflowY: 'auto' }}>
                {rows.map((m, idx) => (
                  <MilestoneRow key={m.key} m={m} idx={idx} count={rows.length} update={update} remove={remove} move={move} disabled={!writable} />
                ))}
              </MDBox>
              {writable && (
                <MDBox mt={1.5} display="flex" gap={1}>
                  <MDButton size="small" variant="text" color="info" onClick={() => setRows((rs) => [...rs, row('', totalOk ? 0 : round2(Math.max(0, 100 - total)), 'DAYS_AFTER_BOOKING', 30)])}>
                    <Icon>add</Icon>&nbsp;Add milestone
                  </MDButton>
                </MDBox>
              )}
            </InfoCard>
          </MDBox>
        </Grid>

        <Grid item xs={12} lg={5}>
          <InfoCard icon="preview" title="Preview">
            <Grid container spacing={1.5} mb={1.5}>
              <Grid item xs={7}>
                <TextField fullWidth size="small" type="number" label={`Sample net price (${defaultCurrency()})`} value={samplePrice} onChange={(e) => setSamplePrice(e.target.value)} />
              </Grid>
              <Grid item xs={5}>
                <DateField label="Booking date" value={sampleDate} onChange={(v) => setSampleDate(v || todayIso())} sx={{ width: '100%' }} />
              </Grid>
            </Grid>
            {!previewBody ? (
              <MDTypography variant="button" color="text">Fix the milestones (they must total 100%) to see a preview.</MDTypography>
            ) : (
              <SchedulePreview {...preview} currency={defaultCurrency()} />
            )}
            <MDTypography variant="caption" color="text" component="p" mt={1}>
              Construction- and handover-linked installments stay undated until progress or handover is recorded on the project.
            </MDTypography>
          </InfoCard>
        </Grid>
      </Grid>
      {snack}
    </RePage>
  );
}
